<template>
  <autocomplete-preset :api="api" :enrich="enrichFn" :fields="fields" :resp-mapper="respMapper"
                       :value="value"
                       v-bind="$props" @input="$emit('input', $event)"/>
</template>
<script lang="ts">
import AutocompleteMixin from '@/mixins/autocompletes/Autocomplete.mixin';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {Employee} from '@/interfaces/employee.interface';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {PickerEnricher, PickerItem, PickerOrigin, PickerSlot, PickerTag} from '@/components/common/entity-picker/entityPicker.types';
import {enrichTeacherItems} from '@/components/common/entity-picker/teacherContext.helper';
import {isValidOrigin} from '@/components/common/entity-picker/entityPicker.helper';

const BASE_FIELDS = ['firstName', 'isApplicant', 'lastName', 'customId', 'status', 'isActive'];
const TEACHER_CONTEXT_FIELDS = ['address', 'subjects', 'isTeacher'];

/** [lon, lat] of a `near` filter, if any */
function originOfFilter(filter: any): PickerOrigin | null {
  const near = filter && filter.near;
  if (near && typeof near.lat === 'number' && typeof near.lon === 'number') {
    const origin = [near.lon, near.lat];
    return isValidOrigin(origin) ? origin : null;
  }
  return null;
}

export default AutocompleteMixin.extend({
  data: () => ({
    enrichFn: undefined as PickerEnricher | undefined,
  }),
  props: {
    disablePotential: {type: Boolean, default: false},
    disabledRules: {
      type: Array as () => Array<(v: any) => string | false>,
      default: () => ([]),
    },
    disableApplicants: {
      type: Boolean,
      default: false,
    },
    appendOuterIcon: {
      type: String,
      default: '',
    },
    /** Optional [lon, lat] of the place the dialog plans for; shows distance and ranks nearby first */
    contextOrigin: {
      type: Array as unknown as () => PickerOrigin | null,
      default: null,
    },
    /** Optional weekday/time the dialog plans for; shows whether the teacher has no own course then */
    contextSlot: {
      type: Object as () => PickerSlot | null,
      default: null,
    },
  },
  computed: {
    api: () => EmployeeHandlerAPI,
    computedDisabledRules(): Array<(v: any) => string | false> {
      const disabledRules = [...this.disabledRules];
      if (this.disableApplicants) {
        disabledRules.push((e) => e.isApplicant && 'Noch im Bewerbungsstatus');
        disabledRules.push((e) => !this.$$crudAccessManager.entity(CrudEntityTypes.EMPLOYEE, e._id).canRead(EmployeeFields.IS_APPLICANT) && 'Kein Lesezugriff auf Bewerbungsstatus');
      }
      return disabledRules;
    },
    effectiveOrigin(): PickerOrigin | null {
      if (isValidOrigin(this.contextOrigin)) {
        return this.contextOrigin;
      }
      return originOfFilter(this.filter) || originOfFilter(this.prefetchFilter);
    },
    /** Teacher facts (distance, subjects, courses, free slot) for teacher searches or when a dialog passes context */
    teacherContext(): boolean {
      const isTeacherFilter = (f: any) => !!f && f.isTeacher === true;
      return isTeacherFilter(this.filter) || isTeacherFilter(this.prefetchFilter)
          || !!this.effectiveOrigin || !!this.contextSlot;
    },
    fields(): string[] {
      return this.teacherContext ? [...BASE_FIELDS, ...TEACHER_CONTEXT_FIELDS] : BASE_FIELDS;
    },
    /** Changes only when the context really changes (filters are often inline objects) */
    contextKey(): string {
      return JSON.stringify({teacher: this.teacherContext, origin: this.effectiveOrigin, slot: this.contextSlot});
    },
  },
  watch: {
    contextKey: {
      immediate: true,
      handler() {
        if (!this.teacherContext) {
          this.enrichFn = undefined;
          return;
        }
        const origin = this.effectiveOrigin;
        const slot = this.contextSlot;
        this.enrichFn = (items: PickerItem[]) => enrichTeacherItems(items, {origin, slot});
      },
    },
  },
  methods: {
    respMapper(resp: { employees: Employee[] }): PickerItem[] {
      return resp.employees.map((employee) => {

        let text = (employee.firstName || employee.lastName) ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : 'Unbekannte Person';

        const tags: PickerTag[] = [];
        if (employee.isApplicant) {
          tags.push({text: 'Bewerber:in', tone: 'warn', icon: 'mdi-account-clock'});
        }
        if (employee.isActive === false) {
          tags.push({text: 'inaktiv', tone: 'muted', icon: 'mdi-account-off'});
        }

        return {
          text,
          value: employee._id,
          description: employee.customId,
          disabled: this.computedDisabledRules.map((fn) => fn(employee)).find((v) => v) ?? false,
          entity: 'employee',
          customId: employee.customId,
          tags,
          source: employee,
        };
      });
    },
  },
});
</script>
